import {
  createAsyncThunk,
  createSlice,
  type PayloadAction
} from "@reduxjs/toolkit"

import * as api from "../api/services.ts"
import type {
  JobListParams,
  MyPermissions,
  Plan3DJob,
  Plan3DJobRow
} from "../api/types.ts"

type LoadStatus = "idle" | "loading" | "failed"

interface Plans3dState {
  jobs: Plan3DJobRow[]
  listStatus: LoadStatus
  listError: string | null
  /** requestId of the newest list fetch; an older one resolving later is dropped. */
  listRequestId: string | null
  activeJob: Plan3DJob | null
  activeJobId: string | null
  /** requestId of the newest job fetch; an older one settling later is dropped. */
  jobRequestId: string | null
  jobStatus: LoadStatus
  jobError: string | null
  permissions: MyPermissions | null
  permissionsStatus: "idle" | "loading" | "loaded" | "failed"
}

const initialState: Plans3dState = {
  jobs: [],
  listStatus: "idle",
  listError: null,
  listRequestId: null,
  activeJob: null,
  activeJobId: null,
  jobRequestId: null,
  jobStatus: "idle",
  jobError: null,
  permissions: null,
  permissionsStatus: "idle"
}

export const fetchJobs = createAsyncThunk(
  "plans3d/fetchJobs",
  (params: JobListParams) => api.listJobs(params)
)
export const fetchJob = createAsyncThunk("plans3d/fetchJob", (id: string) =>
  api.getJob(id)
)
export const fetchMyPermissions = createAsyncThunk("plans3d/me", () =>
  api.getMyPermissions()
)

const isOtherJob = (state: Plans3dState, id: string) =>
  state.activeJobId !== null && state.activeJobId !== id

// Some status moves do not bump row_version, so a tie is broken on `updated`.
function isOlder(incoming: Plan3DJob, held: Plan3DJob | null): boolean {
  if (!held || held.id !== incoming.id) return false
  if (incoming.row_version !== held.row_version)
    return incoming.row_version < held.row_version
  const a = Date.parse(incoming.updated)
  const b = Date.parse(held.updated)
  return !Number.isNaN(a) && !Number.isNaN(b) && a < b
}

const slice = createSlice({
  name: "plans3d",
  initialState,
  reducers: {
    setActiveJob(state, action: PayloadAction<Plan3DJob>) {
      if (isOlder(action.payload, state.activeJob)) return
      state.activeJob = action.payload
      state.activeJobId = action.payload.id
      state.jobError = null
    },
    openJob(state, action: PayloadAction<string>) {
      if (state.activeJobId !== action.payload) {
        state.activeJob = null
        state.jobError = null
      }
      state.activeJobId = action.payload
    },
    clearActiveJob(state) {
      state.activeJob = null
      state.activeJobId = null
      state.jobRequestId = null
      state.jobStatus = "idle"
      state.jobError = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobs.pending, (state, action) => {
        state.listStatus = "loading"
        state.listRequestId = action.meta.requestId
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return
        state.jobs = action.payload
        state.listStatus = "idle"
        state.listError = null
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        if (state.listRequestId !== action.meta.requestId) return
        state.listStatus = "failed"
        state.listError = action.error.message ?? null
      })
      .addCase(fetchJob.pending, (state, action) => {
        state.jobStatus = "loading"
        state.jobRequestId = action.meta.requestId
      })
      .addCase(fetchJob.fulfilled, (state, action) => {
        if (state.jobRequestId !== action.meta.requestId) return
        state.jobStatus = "idle"
        if (
          isOtherJob(state, action.payload.id) ||
          isOlder(action.payload, state.activeJob)
        )
          return
        state.activeJob = action.payload
        state.activeJobId = action.payload.id
        state.jobError = null
      })
      .addCase(fetchJob.rejected, (state, action) => {
        if (state.jobRequestId !== action.meta.requestId) return
        // An abort is the workspace leaving (or a poll being replaced), not a failure to report.
        if (action.meta.aborted || isOtherJob(state, action.meta.arg)) {
          state.jobStatus = "idle"
          return
        }
        state.jobStatus = "failed"
        state.jobError = action.error.message ?? null
      })
      .addCase(fetchMyPermissions.pending, (state) => {
        state.permissionsStatus = "loading"
      })
      .addCase(fetchMyPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload
        state.permissionsStatus = "loaded"
      })
      .addCase(fetchMyPermissions.rejected, (state) => {
        state.permissions = null
        state.permissionsStatus = "failed"
      })
  }
})

export const { setActiveJob, openJob, clearActiveJob } = slice.actions
export const plans3d = slice.reducer
